import { useEffect, useMemo, useState } from "react";
import _useWebSocket, { ReadyState } from "react-use-websocket";
import type { WebSocketHook } from "react-use-websocket/dist/lib/types";
import { API_WS_URL } from "../api/auth";
import { useClerkToken } from "./use-clerk-token";

export const WS_CONNECTION_STATUS = {
  [ReadyState.CONNECTING]: "Connecting",
  [ReadyState.OPEN]: "Open",
  [ReadyState.CLOSING]: "Closing",
  [ReadyState.CLOSED]: "Closed",
  [ReadyState.UNINSTANTIATED]: "Uninstantiated",
};

export type WebSocketHookType<T, O> = WebSocketHook<O> & {
  messages: O[];
  send: (_: T) => void;
  connectionStatus: string;
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const useWebSocket = <T = any, O = any>(
  path: string,
  onMessageCallback?: (message: O) => void,
  useAuth: boolean = true,
  queryParams?: Record<string, string>,
  tokenOverride?: string,
): WebSocketHookType<T, O> => {
  const { token } = useClerkToken();
  const [usedToken, setUsedToken] = useState<string | null>(null);
  const [messages, setMessages] = useState<O[]>([]);

  const finalToken = useMemo(() => tokenOverride || token, [tokenOverride, token]);

  useEffect(() => {
    if (usedToken) return;
    if (!finalToken) return;
    setUsedToken(finalToken);
  }, [usedToken, finalToken]);

  const all = _useWebSocket(
    `${API_WS_URL}/${path.replace(/^\/+/, "")}`,
    {
      queryParams: {
        ...queryParams,
        api_key: finalToken!,
      },
      onMessage: (message) => {
        const parsed = JSON.parse(message.data) as O;
        setMessages((prev) => [...prev, parsed]);
        onMessageCallback?.(parsed);
      },
    },
    useAuth ? !!finalToken : true,
  );

  return {
    ...(all as WebSocketHook<O>),
    messages,
    send: (data: T) => all.sendJsonMessage(data),
    connectionStatus: WS_CONNECTION_STATUS[all.readyState],
  };
};
